import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { deleteReport, listReports, withBase } from "./api";
import styles from "./List.module.css";

const STATUS_OPTIONS = ["Active", "Inactive"];

export default function ReportsList({ filterType: filterTypeProp }) {
  const { type } = useParams();
  const filterType = filterTypeProp || type || '';
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(8);

  useEffect(() => {
    loadReports();
  }, [filterType]);

  async function loadReports() {
    setError("");
    try {
      const res = await listReports(filterType);
      setItems(res.items || []);
    } catch (err) {
      setError(err.message || "Failed to load reports");
    }
  }

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchesQuery = item.title.toLowerCase().includes(query.toLowerCase()) ||
        item.description.toLowerCase().includes(query.toLowerCase()) ||
        item.financial_year.toLowerCase().includes(query.toLowerCase());
      const matchesStatus = statusFilter === "" || item.status === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [items, query, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / limit));
  const visibleItems = filteredItems.slice((page - 1) * limit, page * limit);

  const pageTitle = filterType === 'audit' ? 'Audit Reports' : filterType === 'annual' ? 'Annual Reports' : 'Reports';

  return (
    <div className={styles.card}>
      <div className={styles.headerRow}>
        <div className={styles.title}>{pageTitle}</div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <button
          className={`${styles.btn} ${styles.btnDownload}`}
          onClick={() => { window.location.href = '/admin/reports/add'; }}
        >
          + Add Report
        </button>
        <div style={{ flex: '1 1 220px' }}>
          <input
            className={styles.input}
            type="search"
            placeholder="Search by title, year, or description"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
        </div>
        <div style={{ minWidth: 200 }}>
          <select
            className={styles.input}
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>
      {error && <div className={styles.empty}>{error}</div>}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            <th className={styles.th}>#</th>
            <th className={styles.th}>Type</th>
            <th className={styles.th}>Financial Year</th>
            <th className={styles.th}>Title</th>
            <th className={styles.th}>Status</th>
            <th className={styles.th}>Publish Date</th>
            <th className={styles.th}>Action</th>
          </tr>
        </thead>
        <tbody>
          {visibleItems.length === 0 ? (
            <tr>
              <td className={styles.empty} colSpan={7}>No reports found.</td>
            </tr>
          ) : (
            visibleItems.map((it, idx) => (
              <tr key={it.id}>
                <td className={styles.td}>{(page - 1) * limit + idx + 1}</td>
                <td className={styles.td}>{it.type === 'audit' ? 'Audit Report' : 'Annual Report'}</td>
                <td className={styles.td}>{it.financial_year}</td>
                <td className={styles.td}>{it.title}</td>
                <td className={styles.td}>{it.status}</td>
                <td className={styles.td}>{it.publish_date}</td>
                <td className={styles.td}>
                  <div className={styles.actions}>
                    <button className={`${styles.btn} ${styles.btnEdit}`} onClick={() => window.location.href = `/admin/reports/edit?id=${it.id}`}>
                      Edit
                    </button>
                    <button className={`${styles.btn} ${styles.btnDownload}`} onClick={() => {
                      window.open(withBase(it.download_url || ''), '_blank');
                    }}>
                      Download
                    </button>
                    <button
                      className={`${styles.btn} ${styles.btnDelete}`}
                      disabled={busyId === it.id}
                      onClick={async () => {
                        if (!window.confirm('Delete this report?')) return;
                        setBusyId(it.id);
                        try {
                          await deleteReport(it.id);
                          setItems(items.filter((x) => x.id !== it.id));
                        } catch (err) {
                          setError(err.message || 'Delete failed');
                        } finally {
                          setBusyId(null);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
        <div>{filteredItems.length} report(s) found</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className={`${styles.btn} ${styles.btnDownload}`} disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>Prev</button>
          <span>Page {page} / {totalPages}</span>
          <button className={`${styles.btn} ${styles.btnDownload}`} disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}>Next</button>
        </div>
      </div>
    </div>
  );
}
